'use client';
import { FilterSelectionProp, GetTemplateLimit, GetTemplatesProps } from '@components/Templates/utils/constants';
import useTimer from '@lib/hooks/useTimer';
import { ThemeProjection } from '@lib/sanity/types/common';
import { LabelValue, LabelValueHint } from '@lib/types';
import { DesignSetIdLayout } from '@ui/Design/types';
import { AnalyticsEvent, sendEvent } from '@utils/segment';
import { DesignSizeTypeTypes, TemplatesQuery } from 'graphql/generated/types.autogenerated';
import { useCallback, useRef, useState } from 'react';
import { getFilters, isType, setUrlQueryParams } from '../../utils/utils';
import Filter from './Filter/Filter';
import Gallery from './Gallery/Gallery';

type Props = {
  designSetLayout?: DesignSetIdLayout;
  initialTemplatesComp?: React.ReactElement;
  initialTemplates?: TemplatesQuery['templates'];
  theme?: ThemeProjection;
  searchParams: { [key: string]: string | string[] };
  db: {
    styles?: LabelValue[];
    colors?: LabelValue[];
    designSizeTypes?: LabelValueHint[];
    categories?: LabelValue[];
  };
  document?: {
    categoryIds?: number[];
    designSizeTypeIds?: number[];
    tagIds?: number[];
  };
  getTemplates: (props: GetTemplatesProps) => Promise<TemplatesQuery['templates'] | undefined>;
  description?: string;
  hideFilters?: boolean;
};

export default function GalleryAndFilter({
  designSetLayout,
  initialTemplatesComp,
  initialTemplates,
  theme,
  db,
  document,
  searchParams,
  getTemplates,
  description,
  hideFilters,
}: Props) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [filterSelection, setFilterSelection] = useState<FilterSelectionProp>({
    ...searchParams,
    page: (searchParams.page as string) ?? '1',
  });
  const [selectedPage, setSelectedPage] = useState(Number((searchParams.page as string) ?? '1'));
  const [templates, setTemplates] = useState<TemplatesQuery['templates'] | undefined>();
  const [isRefetched, setIsRefetched] = useState(false);

  const lastFilter = useRef<FilterSelectionProp>(filterSelection);
  const pagesViewedRef = useRef<{ page: number; templatesCount: number }[]>([
    {
      page: selectedPage,
      templatesCount: initialTemplates?.templates?.length ?? 0,
    },
  ]);

  useTimer((time: number) => {
    const secondsOnPage = Math.round(time / 1000);
    const scrollPosition = window.document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((window.scrollY / scrollPosition) * 100);

    sendEvent(AnalyticsEvent.ScrollDepth, {
      secondsOnPage,
      scrollPercent,
      pagesViewed: pagesViewedRef.current,
    });
  });

  const reFetchTemplates = useCallback(
    async (filter: FilterSelectionProp, pageNum = selectedPage) => {
      const filters = getFilters({
        tag: {
          dbColors: db.colors,
          dbStyles: db.styles,
          documentTags: document?.tagIds,
        },
        category: {
          dbCategories: db.categories,
          documentCategories: document?.categoryIds,
        },
        size: {
          dbDesignSizeTypes: db.designSizeTypes,
          documentDesignSizeTypes: document?.designSizeTypeIds,
        },
        filterSelection: filter,
      });
      const res = await getTemplates({
        ...filters,
        offset: (pageNum - 1) * GetTemplateLimit,
      });

      if (isFilterChanged(lastFilter.current, filter)) {
        // User changed a filter
        sendEvent(AnalyticsEvent.FilterApplied, {
          document,
          filter,
          totalCount: res?.totalCount ?? 0,
        });
      }
      lastFilter.current = filter;
      pagesViewedRef.current.push({
        page: pageNum,
        templatesCount: res?.templates?.length ?? 0,
      });

      setIsRefetched(true);
      setTemplates(res);
    },
    [selectedPage, db, document, getTemplates],
  );

  function onSelectChange(data?: FilterSelectionProp, pageNum = 1) {
    setSelectedPage(pageNum);
    setFilterSelection({ ...filterSelection, ...data });
    setUrlQueryParams({
      ...filterSelection,
      ...data,
      page: data?.page ?? pageNum,
    });
    reFetchTemplates({ ...filterSelection, ...data }, pageNum);
  }

  return (
    <div ref={elementRef}>
      {!hideFilters && (
        <Filter
          theme={theme}
          onSelectChange={onSelectChange}
          selectedItems={filterSelection}
          hideTypeFilter={
            initialTemplates?.templates.some((x) => x.hasVideo) || templates?.templates.some((x) => x.hasVideo)
          }
          db={db}
        />
      )}
      <Gallery
        designSetLayout={designSetLayout}
        elementRef={elementRef}
        isRefetched={isRefetched}
        onSelectChange={onSelectChange}
        selectedPage={selectedPage}
        initialTemplatesComp={initialTemplatesComp}
        initialTemplates={initialTemplates}
        fetchedTemplates={templates}
        isGridView={filterSelection.size?.length === 1 || document?.designSizeTypeIds?.length === 1}
        isStaticFilterSelected={isType(DesignSizeTypeTypes.STATIC, filterSelection)}
        description={description}
      />
    </div>
  );
}

function isFilterChanged(prev: FilterSelectionProp, next: FilterSelectionProp): boolean {
  const keys: (keyof FilterSelectionProp)[] = ['color', 'style', 'size', 'type', 'industry', 'query'];
  return keys.some((key) => {
    const prevValue = Array.isArray(prev[key]) ? (prev[key] as string[]) : [prev[key] as string];
    const nextValue = Array.isArray(next[key]) ? (next[key] as string[]) : [next[key] as string];
    return !arraysEqual(prevValue, nextValue);
  });
}

function arraysEqual(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((value, index) => value === arr2[index]);
}
